import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { z } from 'zod';
import {
  DRILL_STATUSES,
  anchorView,
  drillView,
  saltHex,
  scanForForbiddenKeys,
  updateProfileInput,
  type UpdateProfileInput,
} from '@recovery/shared';
import { firebaseAuth, firebaseDb } from './firebase';

const anchorRowSchema = anchorView.extend({ recoveryCodeSalt: saltHex });
const drillRowSchema = drillView.extend({ status: z.enum(DRILL_STATUSES) });

export type AnchorRow = z.infer<typeof anchorRowSchema>;
export type DrillRow = z.infer<typeof drillRowSchema>;

export function watchUser(onChange: (user: User | null) => void): Unsubscribe {
  return onAuthStateChanged(firebaseAuth(), onChange);
}

export async function signIn(): Promise<void> {
  await signInWithPopup(firebaseAuth(), new GoogleAuthProvider());
}

export async function signOutUser(): Promise<void> {
  await signOut(firebaseAuth());
}

export function watchAnchors(uid: string, onChange: (rows: AnchorRow[]) => void): Unsubscribe {
  const anchors = query(
    collection(firebaseDb(), 'anchors'),
    where('ownerUid', '==', uid),
    orderBy('nextDueAt', 'asc'),
  );
  return onSnapshot(
    anchors,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) =>
        anchorRowSchema.parse({ id: entry.id, ...entry.data() }),
      );
      onChange(rows);
    },
    () => onChange([]),
  );
}

export function watchOpenDrills(uid: string, onChange: (rows: DrillRow[]) => void): Unsubscribe {
  const drills = query(
    collection(firebaseDb(), 'drills'),
    where('ownerUid', '==', uid),
    where('status', 'in', ['scheduled', 'started']),
    orderBy('scheduledFor', 'asc'),
  );
  return onSnapshot(
    drills,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) =>
        drillRowSchema.parse({ id: entry.id, ...entry.data() }),
      );
      onChange(rows);
    },
    () => onChange([]),
  );
}

export type DeviceStateReport = Pick<
  UpdateProfileInput,
  'localVaultStatus' | 'persistGranted' | 'installedAsPWA' | 'timezone'
>;

export async function reportDeviceState(uid: string, input: DeviceStateReport): Promise<void> {
  const scan = scanForForbiddenKeys(input);
  if (!scan.ok) throw new Error(`Profile update rejected at field path: ${scan.path || '<root>'}.`);

  const parsed = updateProfileInput.safeParse(input);
  if (!parsed.success) throw new Error('Profile update does not match the server contract.');

  await updateDoc(doc(firebaseDb(), 'users', uid), {
    ...parsed.data,
    updatedAt: Date.now(),
  });
}

export async function updateUserLocale(uid: string, locale: string): Promise<void> {
  const parsed = updateProfileInput.safeParse({ locale });
  if (!parsed.success) return;

  await updateDoc(doc(firebaseDb(), 'users', uid), {
    locale: parsed.data.locale,
    updatedAt: Date.now(),
  });
}
